import type { PendingForm } from '@/api/types/Forms'
import type {
	PatientBillingChargeStatus,
	PatientBillingRecord,
	PaymentPlan,
} from '@/api/types/PatientApi'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { format } from 'date-fns'

/**
 * The home screen as a list of tiles that follow the state of the account.
 *
 * Pure: every read comes in as an input, `undefined` while it is in flight and `null` when it
 * failed, so a failed read is never mistaken for an empty one (a billing record that could not
 * be read must not say "Up to date"). Nothing here touches React, so it runs under jest as is.
 */

/** `undefined` while loading, `null` when the read failed. */
export type Loadable<T> = T | null | undefined

export type HomeResults = { appointmentId?: number; healthScore?: number }

export type HomeTilesInput = {
	now: Date
	firstName?: string
	visits: Loadable<UpcomingVisit[]>
	pendingForms: Loadable<PendingForm[]>
	results: Loadable<HomeResults>
	seenResultsAppointmentId?: number
	billing: Loadable<PatientBillingRecord>
	plans: Loadable<PaymentPlan[]>
	referral: Loadable<{ discountInCents: number }>
	supportUnread?: number
}

export type NextPayment = { amountInCents: number; dueOn: string }

export type ScoreCard = {
	id: 'score'
	state: 'loading' | 'locked' | 'ready'
	healthScore?: number
}
export type BillingCard = {
	id: 'billing'
	state: 'loading' | 'unknown' | 'attention' | 'next' | 'up-to-date'
	next?: NextPayment
}
export type ReferCard = {
	id: 'refer'
	state: 'loading' | 'hidden' | 'ready'
	discountInCents?: number
}
export type SupportCard = { id: 'support'; unread?: number }

export type HomeTile =
	| { kind: 'greeting'; text: string }
	| { kind: 'hero'; hero: 'payment-attention' }
	| { kind: 'hero'; hero: 'score-ready'; appointmentId: number; healthScore?: number }
	| { kind: 'visit-loading' }
	| { kind: 'visit-unavailable' }
	| {
			kind: 'visit'
			visit: UpcomingVisit
			pendingForms: PendingForm[]
			moreCount: number
	  }
	| { kind: 'book-visit' }
	| { kind: 'todo-form'; form: PendingForm }
	| { kind: 'grid'; cards: [ScoreCard, BillingCard, ReferCard, SupportCard] }

const ATTENTION_STATUSES: ReadonlySet<PatientBillingChargeStatus> = new Set([
	'FAILED',
	'EXPIRED',
	'DISPUTED',
])

const SETTLED_STATUSES: ReadonlySet<PatientBillingChargeStatus> = new Set([
	'SUCCEEDED',
	'REFUNDED',
	'PARTIALLY_REFUNDED',
	'CANCELED',
])

const toDay = (date: string) => date.slice(0, 10)

export const greeting = (now: Date, firstName?: string) => {
	const hour = now.getHours()
	const timeOfDay =
		hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
	return firstName ? `${timeOfDay}, ${firstName}` : timeOfDay
}

/** The forms issued for this visit, by the scheduling link they share. */
export const matchFormsToVisit = (visit: UpcomingVisit, forms: PendingForm[]) =>
	visit.scheduleLinkId
		? forms.filter((form) => form.config?.schedulingLinkId === visit.scheduleLinkId)
		: []

/**
 * The forms no loaded visit claims: issued without a link, or for a link no visit carries
 * (a visit rebooked through a reschedule link has a new one). These are the home to-dos.
 */
export const unmatchedForms = (forms: PendingForm[], visits: UpcomingVisit[]) => {
	const linkIds = new Set(visits.map((visit) => visit.scheduleLinkId).filter(Boolean))
	return forms.filter((form) => {
		const linkId = form.config?.schedulingLinkId
		return !linkId || !linkIds.has(linkId)
	})
}

const newestCharge = (billing: PatientBillingRecord | null) => {
	const charges = billing?.charges ?? []
	return charges.reduce<PatientBillingRecord['charges'][number] | undefined>(
		(newest, charge) => (!newest || charge.date > newest.date ? charge : newest),
		undefined
	)
}

const instalmentsOf = (plans: PaymentPlan[] | null) =>
	(plans ?? []).flatMap((plan) => plan.instalments)

const isSettled = (status: PatientBillingChargeStatus | null) =>
	status !== null && SETTLED_STATUSES.has(status)

/**
 * A decline or a missed instalment the patient should know about. Only the newest charge
 * counts: the charge list is the whole history, and a decline from a year ago that was since
 * paid must not pin the hero forever.
 */
export const needsPaymentAttention = (
	billing: PatientBillingRecord | null,
	plans: PaymentPlan[] | null,
	now: Date
) => {
	const today = format(now, 'yyyy-MM-dd')
	const latest = newestCharge(billing)
	if (latest && ATTENTION_STATUSES.has(latest.status)) {
		return true
	}
	return instalmentsOf(plans).some(
		(instalment) =>
			(instalment.paymentStatus !== null &&
				ATTENTION_STATUSES.has(instalment.paymentStatus)) ||
			(toDay(instalment.dueOn) < today && !isSettled(instalment.paymentStatus))
	)
}

/** The soonest amount still to come: the membership renewal or an unpaid instalment. */
export const nextPayment = (
	billing: PatientBillingRecord | null,
	plans: PaymentPlan[] | null,
	now: Date
): NextPayment | undefined => {
	const today = format(now, 'yyyy-MM-dd')
	const candidates: NextPayment[] = []

	const subscription = billing?.subscription
	if (subscription && subscription.status === 'ACTIVE' && subscription.renewalDate) {
		candidates.push({
			amountInCents: subscription.costInCents,
			dueOn: toDay(subscription.renewalDate),
		})
	}

	for (const instalment of instalmentsOf(plans)) {
		const dueOn = toDay(instalment.dueOn)
		if (dueOn >= today && !isSettled(instalment.paymentStatus)) {
			candidates.push({ amountInCents: instalment.amountInCents, dueOn })
		}
	}

	return candidates.sort((a, b) => a.dueOn.localeCompare(b.dueOn))[0]
}

const heroTile = (input: HomeTilesInput): HomeTile | undefined => {
	const { billing, plans, results, seenResultsAppointmentId, now } = input

	if (
		billing !== undefined &&
		plans !== undefined &&
		needsPaymentAttention(billing, plans, now)
	) {
		return { kind: 'hero', hero: 'payment-attention' }
	}

	if (
		results &&
		results.appointmentId !== undefined &&
		results.appointmentId !== seenResultsAppointmentId
	) {
		return {
			kind: 'hero',
			hero: 'score-ready',
			appointmentId: results.appointmentId,
			healthScore: results.healthScore,
		}
	}

	return undefined
}

const visitTile = (input: HomeTilesInput): HomeTile => {
	const { visits, pendingForms } = input
	if (visits === undefined) {
		return { kind: 'visit-loading' }
	}
	if (visits === null) {
		return { kind: 'visit-unavailable' }
	}
	const next = visits[0]
	if (!next) {
		return { kind: 'book-visit' }
	}
	return {
		kind: 'visit',
		visit: next,
		pendingForms: matchFormsToVisit(next, pendingForms ?? []),
		moreCount: visits.length - 1,
	}
}

const todoTiles = (input: HomeTilesInput): HomeTile[] => {
	const { pendingForms, visits } = input
	if (!pendingForms) {
		return []
	}
	// Linked forms may yet turn out to belong to a visit still loading; unlinked ones cannot.
	const forms =
		visits === undefined
			? pendingForms.filter((form) => !form.config?.schedulingLinkId)
			: unmatchedForms(pendingForms, visits ?? [])
	return forms.map((form) => ({ kind: 'todo-form', form }))
}

const scoreCard = (results: Loadable<HomeResults>): ScoreCard => {
	if (results === undefined) {
		return { id: 'score', state: 'loading' }
	}
	if (results && results.appointmentId !== undefined) {
		return { id: 'score', state: 'ready', healthScore: results.healthScore }
	}
	return { id: 'score', state: 'locked' }
}

const billingCard = (input: HomeTilesInput): BillingCard => {
	const { billing, plans, now } = input
	if (billing === undefined || plans === undefined) {
		return { id: 'billing', state: 'loading' }
	}
	if (needsPaymentAttention(billing, plans, now)) {
		return { id: 'billing', state: 'attention' }
	}
	if (billing === null) {
		return { id: 'billing', state: 'unknown' }
	}
	const next = nextPayment(billing, plans, now)
	return next
		? { id: 'billing', state: 'next', next }
		: { id: 'billing', state: 'up-to-date' }
}

const referCard = (referral: HomeTilesInput['referral']): ReferCard => {
	if (referral === undefined) {
		return { id: 'refer', state: 'loading' }
	}
	if (referral === null) {
		return { id: 'refer', state: 'hidden' }
	}
	return { id: 'refer', state: 'ready', discountInCents: referral.discountInCents }
}

export const buildHomeTiles = (input: HomeTilesInput): HomeTile[] => {
	const tiles: HomeTile[] = [
		{ kind: 'greeting', text: greeting(input.now, input.firstName) },
	]

	const hero = heroTile(input)
	if (hero) {
		tiles.push(hero)
	}

	tiles.push(visitTile(input))
	tiles.push(...todoTiles(input))
	tiles.push({
		kind: 'grid',
		cards: [
			scoreCard(input.results),
			billingCard(input),
			referCard(input.referral),
			{ id: 'support', unread: input.supportUnread },
		],
	})

	return tiles
}
